import React, { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Brain, ShieldAlert, CheckCircle2, XCircle, AlertTriangle, PlayCircle, Home } from "lucide-react";

const HH_STATE_COLORS = {
  resolved: "text-emerald-700", missing_identity: "text-rose-700", ambiguous: "text-orange-700",
  no_current_coverage: "text-slate-600", unresolved: "text-rose-700", unknown: "text-slate-600",
};

const STATE_COLORS = {
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  not_present: "bg-amber-100 text-amber-800 border-amber-300",
  ambiguous: "bg-orange-100 text-orange-800 border-orange-300",
  clarification_required: "bg-orange-100 text-orange-800 border-orange-300",
  verification_required: "bg-sky-100 text-sky-800 border-sky-300",
  unknown: "bg-slate-100 text-slate-700 border-slate-300",
  unresolved: "bg-rose-100 text-rose-800 border-rose-300",
  missing: "bg-rose-100 text-rose-800 border-rose-300",
  not_applicable: "bg-slate-100 text-slate-700 border-slate-300",
};

const StateBadge = ({ state }) => (
  <span className={`inline-block px-2 py-0.5 rounded-md border text-xs font-semibold ${STATE_COLORS[state] || "bg-slate-100 text-slate-700 border-slate-300"}`}>
    {state || "—"}
  </span>
);

const Section = ({ title, children, testid }) => (
  <div className="rounded-xl border-2 border-[#E5E5E0] bg-white overflow-hidden" data-testid={testid}>
    <div className="px-4 py-2.5 bg-[#F8F8F5] border-b font-bold text-[#0B192C] text-sm">{title}</div>
    <div className="p-4 text-sm">{children}</div>
  </div>
);

const Pre = ({ obj }) => (
  <pre className="text-xs bg-[#0B192C] text-emerald-100 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">
    {JSON.stringify(obj, null, 2)}
  </pre>
);

const EXAMPLES = [
  { label: "A. No plan context", q: "Does my plan cover Eliquis?", ctx: "{}" },
  { label: "B. Carrier ≠ plan", q: "Does my Humana plan cover Eliquis?", ctx: "{}" },
  { label: "H. Specialist copay (plan)", q: "What's my specialist copay?", ctx: '{\n  "plan_year": 2026,\n  "contract_id": "H1036",\n  "plan_id": "001",\n  "segment_id": "000"\n}' },
  { label: "I. Personalized drug cost", q: "How much will Eliquis cost me this month?", ctx: '{\n  "plan_year": 2026,\n  "contract_id": "H1036",\n  "plan_id": "001",\n  "segment_id": "000"\n}' },
  { label: "J. Suitability (unsupported)", q: "Tell me whether H1234-001 is good for me.", ctx: "{}" },
  { label: "K. Year substitution", q: "Use my 2026 plan to tell me my 2027 benefits.", ctx: '{\n  "plan_year": 2026,\n  "contract_id": "H1036",\n  "plan_id": "001",\n  "segment_id": "000"\n}' },
];

export default function AskOrchestratorQA() {
  const [question, setQuestion] = useState("Does my plan cover Eliquis?");
  const [ctxText, setCtxText] = useState("{}");
  const [interpText, setInterpText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [householdId, setHouseholdId] = useState("");
  const [hhCtx, setHhCtx] = useState(null);
  const [hhLoading, setHhLoading] = useState(false);
  const [hhErr, setHhErr] = useState(null);

  const resolveHousehold = async () => {
    if (!householdId.trim()) { setHhErr("Enter a household id."); return; }
    setHhLoading(true); setHhErr(null); setHhCtx(null);
    try {
      const r = await api.get("/admin/ask-orchestrator/household-context", { params: { household_id: householdId.trim() } });
      setHhCtx(r.data);
      // convenience: prefill confirmed context with household_id so the question uses it
      setCtxText(JSON.stringify({ household_id: householdId.trim() }, null, 2));
    } catch (e) {
      setHhErr(e?.response?.status === 403 ? "403 — not authorized for this household." : (e?.response?.data?.detail || "Lookup failed."));
    } finally { setHhLoading(false); }
  };

  const run = async () => {
    setLoading(true); setErr(null); setResult(null);
    let confirmed_context = {}; let interpretation;
    try { confirmed_context = ctxText.trim() ? JSON.parse(ctxText) : {}; }
    catch { setErr("Confirmed context is not valid JSON."); setLoading(false); return; }
    if (interpText.trim()) {
      try { interpretation = JSON.parse(interpText); }
      catch { setErr("Interpretation override is not valid JSON."); setLoading(false); return; }
    }
    try {
      const body = { question, confirmed_context };
      if (interpretation) body.interpretation = interpretation;
      const r = await api.post("/admin/ask-orchestrator/interpret", body);
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Request failed.");
    } finally { setLoading(false); }
  };

  const loadExample = (ex) => { setQuestion(ex.q); setCtxText(ex.ctx); setInterpText(""); setResult(null); setErr(null); };

  const re = result?.resolved_entities || {};
  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="ask-orchestrator-qa-page">
      <PageHeader title="Ask Nauticus — Orchestrator QA" icon={Brain}
        subtitle="Internal only. LLM interprets language; authoritative IDs come from confirmed context or deterministic resolvers. Never consumer-exposed." />

      <div className="mt-4 rounded-lg bg-[#FFF7E6] border-2 border-[#D4AF37]/40 px-4 py-2.5 text-sm text-[#7A5C00] flex items-center gap-2" data-testid="orch-safety-banner">
        <ShieldAlert className="w-4 h-4 flex-shrink-0" />
        LLM interpretation ≠ authoritative fact. The gateway only executes when every required identifier is independently resolved.
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <Section title="Confirmed household plan context (optional)" testid="orch-household-section">
          <div className="text-xs text-[#6B7280] mb-2">Resolve an exact CMS plan identity from a household's confirmed coverage record. Ownership is enforced. Resolving prefills the confirmed-context below with this <code>household_id</code>.</div>
          <div className="flex gap-2">
            <Input data-testid="orch-household-input" value={householdId} onChange={(e) => setHouseholdId(e.target.value)}
              placeholder="household_id" className="font-mono text-xs" />
            <Button data-testid="orch-household-resolve-btn" onClick={resolveHousehold} disabled={hhLoading} variant="outline">
              <Home className="w-4 h-4 mr-1.5" /> {hhLoading ? "Resolving…" : "Resolve"}
            </Button>
          </div>
          {hhErr && <p data-testid="orch-household-error" className="mt-2 text-rose-600 text-sm">{hhErr}</p>}
          {hhCtx && (
            <div className="mt-3" data-testid="orch-household-result">
              <div className="text-sm font-bold mb-1">State: <span className={HH_STATE_COLORS[hhCtx.state] || "text-slate-700"}>{hhCtx.state}</span></div>
              {hhCtx.state === "resolved" && (
                <div className="text-xs text-[#0B192C] mb-1">
                  <b>{hhCtx.contract_id}-{hhCtx.pbp}-{hhCtx.segment_id} / {hhCtx.plan_year}</b>
                  {" — "}{hhCtx.provenance?.organization_marketing_name || hhCtx.provenance?.plan_name_cms}
                  {" (source: "}{hhCtx.source_type}{")"}
                </div>
              )}
              <Pre obj={hhCtx} />
            </div>
          )}
        </Section>

        <Section title="Consumer-style question" testid="orch-input-section">
          <Textarea data-testid="orch-question-input" value={question} onChange={(e) => setQuestion(e.target.value)}
            className="min-h-[70px]" placeholder="e.g. Does my plan cover Eliquis?" />
          <div className="mt-3 text-xs font-semibold text-[#6B7280]">Confirmed structured context (JSON — optional authoritative test input)</div>
          <Textarea data-testid="orch-context-input" value={ctxText} onChange={(e) => setCtxText(e.target.value)}
            className="min-h-[120px] font-mono text-xs mt-1" placeholder='{ "plan_year": 2026, "contract_id": "H1036", "plan_id": "001", "segment_id": "000", "rxcui": "1364430" }' />
          <div className="mt-3 text-xs font-semibold text-[#6B7280]">Interpretation override (JSON — optional, skips the LLM)</div>
          <Textarea data-testid="orch-interpretation-input" value={interpText} onChange={(e) => setInterpText(e.target.value)}
            className="min-h-[70px] font-mono text-xs mt-1" placeholder='{ "intent": "drug.coverage", "mentions": { "drug": ["Eliquis"] } }' />
          <Button data-testid="orch-run-btn" onClick={run} disabled={loading} className="mt-3 bg-[#0B192C] hover:bg-[#0B192C]/90">
            <PlayCircle className="w-4 h-4 mr-1.5" /> {loading ? "Interpreting…" : "Interpret & gate"}
          </Button>
          {err && <p data-testid="orch-error" className="mt-2 text-rose-600 text-sm">{err}</p>}
        </Section>

        <Section title="Adversarial / safety examples" testid="orch-examples-section">
          <div className="grid gap-2">
            {EXAMPLES.map((ex) => (
              <button key={ex.label} data-testid={`orch-example-${ex.label[0]}`} onClick={() => loadExample(ex)}
                className="text-left rounded-lg border border-[#E5E5E0] hover:border-[#D4AF37] px-3 py-2 transition-colors">
                <div className="font-semibold text-[#0B192C] text-xs">{ex.label}</div>
                <div className="text-[#6B7280] text-xs mt-0.5">{ex.q}</div>
              </button>
            ))}
          </div>
        </Section>
      </div>

      {result && (
        <div className="mt-6 space-y-4" data-testid="orch-result">
          <Section title="1–2. Interpreted intent & extracted mentions" testid="orch-intent">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-[#0B192C]">{result.interpreted_intent}</Badge>
              {result.capability_id
                ? <span className="text-xs text-[#6B7280]">capability: <b>{result.capability_id}</b></span>
                : <span className="text-xs text-[#6B7280]">no registered capability</span>}
              <span className="text-xs text-[#6B7280]">confidence (advisory): {result.intent_confidence}</span>
              <span className="text-xs text-[#6B7280]">via: {result.interpretation_source}</span>
            </div>
            <div className="mt-3"><div className="text-xs font-semibold text-[#6B7280] mb-1">Human-language mentions (NOT identifiers)</div>
              <Pre obj={result.extracted_mentions} /></div>
          </Section>

          <Section title="3–5. Authoritative identifiers resolved (with source)" testid="orch-resolved">
            {Object.keys(re).length === 0 && <p className="text-[#6B7280]">None.</p>}
            <div className="grid gap-2">
              {Object.entries(re).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-[#EEE] p-2.5" data-testid={`orch-entity-${k}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <b className="text-[#0B192C] text-xs">{k}</b>
                    <StateBadge state={v.state} />
                    {v.source && <span className="text-[11px] text-[#6B7280]">source: {v.source}</span>}
                    {v.value && typeof v.value !== "object" && <span className="text-xs font-mono">{String(v.value)}</span>}
                  </div>
                  {(v.reason || v.note || v.value && typeof v.value === "object" || v.options) && (
                    <div className="mt-1.5"><Pre obj={v} /></div>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {(result.ambiguity_options && Object.keys(result.ambiguity_options).length > 0) && (
            <Section title="6. Ambiguity — clarification required (LLM does NOT choose)" testid="orch-ambiguity">
              <Pre obj={result.ambiguity_options} />
            </Section>
          )}

          {(result.missing_parameters?.length > 0 || result.unresolved_entities?.length > 0) && (
            <Section title="7. Missing parameters / unresolved identifiers" testid="orch-missing">
              {result.missing_parameters?.length > 0 && <p className="text-sm">Missing: <b>{result.missing_parameters.join(", ")}</b></p>}
              {result.unresolved_entities?.length > 0 && <div className="mt-2"><Pre obj={result.unresolved_entities} /></div>}
            </Section>
          )}

          <Section title="8–10. Execution gate & gateway result" testid="orch-gate">
            <div className="flex items-center gap-2 text-sm font-semibold" data-testid="orch-gate-decision">
              {result.safe_to_execute
                ? <><CheckCircle2 className="w-5 h-5 text-emerald-600" /> Gateway execution ALLOWED</>
                : <><XCircle className="w-5 h-5 text-rose-600" /> Gateway execution BLOCKED</>}
            </div>
            {result.explanation_status && <div className="mt-2"><Pre obj={result.explanation_status} /></div>}
            {result.gateway_request && (
              <div className="mt-3"><div className="text-xs font-semibold text-[#6B7280] mb-1">Gateway request</div><Pre obj={result.gateway_request} /></div>
            )}
            {result.gateway_result && (
              <div className="mt-3">
                <div className="text-xs font-semibold text-[#6B7280] mb-1">Gateway result (state: <StateBadge state={result.gateway_result.state} />)</div>
                <Pre obj={result.gateway_result} />
              </div>
            )}
          </Section>

          {result.warnings?.length > 0 && (
            <Section title="11. Warnings" testid="orch-warnings">
              <ul className="space-y-1.5">
                {result.warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {w}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}
